import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import {
  LayoutDashboard, Users, ListOrdered, ShoppingBag, Server, CreditCard,
  Receipt, RotateCcw, ScrollText, LifeBuoy, Settings, LogOut, Menu, X,
  ShieldCheck, Workflow, Bell, Smartphone, Factory, Wallet, Briefcase,
  ChevronDown, ChevronRight, FileText, Coins, AlarmClock,
  Sun, Moon, Tags, Boxes, BadgeDollarSign, DollarSign, Network, Banknote, ShieldAlert, Gauge, UserX, Activity, MessageSquare,
} from "lucide-react";
import api from "@/lib/api";
import { hasPerm } from "@/lib/perms";
import { UserAvatar } from "@/components/shared/UserAvatar";
import { changeAvatar } from "@/lib/avatarUpload";
import { Logo } from "@/components/shared/Logo";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { StaffNotifBell } from "@/components/shared/StaffNotifBell";
import { PushToggle } from "@/components/support/PushToggle";
import { WelcomeLangBanner } from "@/components/shared/WelcomeLangBanner";

// YouTube-Studio-style navigation. 8 top-level sections; every existing route
// (and its permission + data-testid) is preserved as a link or sub-item.
const DASHBOARD = { to: "/admin", label: "Vue d'ensemble", tkey: "admin.nav.dashboard", icon: LayoutDashboard, end: true, perm: "VIEW_ADMIN" };

const NAV = [
  { type: "link", to: "/admin/agents", label: "Agents", tkey: "admin.nav.agents_v2", icon: Briefcase, perm: "MANAGE_AGENTS" },
  { type: "link", to: "/admin/orders", label: "Commandes", tkey: "admin.nav.orders", icon: ListOrdered, perm: "VIEW_ORDERS" },
  { type: "group", key: "clients", label: "Clients", tkey: "admin.nav.clients", icon: Users, badgeKey: "clients", items: [
    { to: "/admin/users", label: "Liste des clients", tkey: "admin.nav.clients_list", icon: Users, perm: "VIEW_CLIENTS" },
    { to: "/admin/support", label: "Support & messages", tkey: "admin.nav.support", icon: LifeBuoy, badgeKey: "support", perm: "VIEW_TICKETS" },
  ] },
  { type: "group", key: "fournisseurs", label: "Fournisseurs", tkey: "admin.nav.providers", icon: Factory, badgeKey: "providers", items: [
    { to: "/admin/providers", label: "Liste des fournisseurs", tkey: "admin.nav.providers_list", icon: Server, perm: "VIEW_PROVIDERS" },
    { to: "/admin/mapping", label: "Mapping", tkey: "admin.nav.mapping", icon: Workflow, perm: "VIEW_PROVIDER_SERVICES" },
    { to: "/admin/catalogue-health", label: "Santé du catalogue", tkey: "admin.nav.catalogue_health", icon: Activity, perm: "VIEW_SERVICES" },
  ] },
  { type: "link", to: "/admin/platforms", label: "Plateformes", tkey: "admin.nav.platforms", icon: Boxes, perm: "VIEW_SERVICES" },
  { type: "link", to: "/admin/services", label: "Services", tkey: "admin.nav.services", icon: ShoppingBag, perm: "VIEW_SERVICES" },
  { type: "link", to: "/admin/service-quality", label: "Qualité des services", tkey: "admin.nav.quality", icon: Gauge, perm: "QUALITY_VIEW" },
  { type: "link", to: "/admin/pricing", label: "Pricing", tkey: "admin.nav.pricing", icon: DollarSign, perm: "PRICING_VIEW" },
  { type: "link", to: "/admin/categories", label: "Catégories", tkey: "admin.nav.categories", icon: Tags, perm: "VIEW_SERVICES" },
  { type: "group", key: "finance", label: "Paiements", tkey: "admin.nav.finance", icon: Wallet, items: [
    { to: "/admin/payments", label: "Paiements", tkey: "admin.nav.payments", icon: CreditCard, perm: "VIEW_PAYMENTS" },
    { to: "/admin/pawapay", label: "PawaPay", tkey: "admin.nav.pawapay", icon: Smartphone, perm: "VIEW_PAWAPAY" },
    { to: "/admin/payment-numbers", label: "Gestion des numéros de paiement", tkey: "admin.nav.payment_numbers", icon: Smartphone, perm: "VIEW_PAYMENT_NUMBERS" },
    { to: "/admin/afripay", label: "AfriPay (Carte)", tkey: "admin.nav.afripay", icon: CreditCard, perm: "VIEW_PAYMENTS" },
    { to: "/admin/usdt", label: "USDT (Crypto)", tkey: "admin.nav.usdt", icon: Coins, perm: "VIEW_PAYMENTS" },
    { to: "/admin/payment-alerts", label: "Alertes paiements", tkey: "admin.nav.payment_alerts", icon: AlarmClock, perm: "VIEW_PAYMENTS" },
    { to: "/admin/transactions", label: "Transactions", tkey: "admin.nav.transactions", icon: Receipt, perm: "VIEW_TRANSACTIONS" },
    { to: "/admin/refunds", label: "Remboursements", tkey: "admin.nav.refunds", icon: RotateCcw, perm: "VIEW_REFUNDS" },
    { to: "/admin/payment-settings", label: "Paramètres paiement", tkey: "admin.nav.payment_settings", icon: CreditCard, perm: "VIEW_PAYMENT_SETTINGS" },
  ] },
  { type: "link", to: "/admin/alerts", label: "Alertes", tkey: "admin.nav.alerts", icon: Bell, badgeKey: "alerts", perm: "VIEW_PROVIDERS" },
  { type: "link", to: "/admin/binary-audit", label: "Traçabilité Binaire", tkey: "admin.nav.binary_audit", icon: Network, perm: "BINARY_AUDIT_VIEW" },
  { type: "link", to: "/admin/risk", label: "Risque & alertes", tkey: "admin.nav.risk", icon: ShieldAlert, perm: "AFFILIATE_RISK_VIEW" },
  { type: "link", to: "/admin/partner-withdrawals", label: "Retraits partenaires", tkey: "admin.nav.partner_withdrawals", icon: Banknote, perm: "AFFILIATE_PAYOUT_VIEW" },
  { type: "group", key: "parametres", label: "Paramètres", tkey: "admin.nav.settings", icon: Settings, items: [
    { to: "/admin/settings", label: "Général", tkey: "admin.nav.settings_general", icon: Settings, perm: "MANAGE_SYSTEM_SETTINGS" },
    { to: "/admin/sms-providers", label: "SMS/OTP Providers", tkey: "admin.nav.sms_providers", icon: MessageSquare, perm: "MANAGE_SYSTEM_SETTINGS" },
    { to: "/admin/security", label: "Accès & sécurité", tkey: "admin.nav.security", icon: ShieldCheck, perm: "VIEW_SECURITY" },
    { to: "/admin/audit-logs", label: "Journaux d'audit", tkey: "admin.nav.audit", icon: ScrollText, perm: "VIEW_AUDIT_LOGS" },
    { to: "/admin/account-deletions", label: "Journal des suppressions", tkey: "admin.nav.account_deletions", icon: UserX, perm: "VIEW_AUDIT_LOGS" },
    { to: "/admin/content/home", label: "Contenu du site", tkey: "admin.nav.content", icon: FileText, perm: "VIEW_SITE_CONTENT" },
  ] },
];

const CRUMB = { "/admin": { tkey: "admin.nav.dashboard", label: "Vue d'ensemble" } };
NAV.forEach((e) => {
  if (e.type === "link") CRUMB[e.to] = { tkey: e.tkey, label: e.label };
  else e.items.forEach((i) => { CRUMB[i.to] = { tkey: i.tkey, label: i.label }; });
});

function useBadges() {
  const [badges, setBadges] = useState({ alerts: 0, clients: 0, providers: 0, support: 0 });
  const loc = useLocation();
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [a, m, s] = await Promise.all([
          api.get("/admin/alerts/count").catch(() => ({ data: { unread: 0 } })),
          api.get("/admin/metrics").catch(() => ({ data: {} })),
          api.get("/admin/support/notifications").catch(() => ({ data: { unread: 0 } })),
        ]);
        if (!alive) return;
        const alerts = a.data.unread || 0;
        setBadges({ alerts, providers: alerts, clients: m.data.pending_orders || 0, support: s.data.unread || 0 });
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [loc.pathname]);
  return badges;
}

function Badge({ count, testid }) {
  if (!count) return null;
  return (
    <span data-testid={testid} className="ml-auto min-w-[20px] rounded-full bg-rose-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function Breadcrumbs() {
  const { t } = useContent();
  const loc = useLocation();
  const path = loc.pathname;
  const hit = CRUMB[path]
    || Object.entries(CRUMB).find(([to]) => to !== "/admin" && path.startsWith(to + "/"))?.[1];
  const label = hit ? t(hit.tkey, hit.label) : t("admin.crumb.root", "Administration");
  return (
    <nav data-testid="admin-breadcrumbs" className="hidden items-center gap-1.5 text-xs text-slate-500 sm:flex dark:text-slate-400">
      <Link to="/admin" className="hover:text-slate-800 dark:hover:text-slate-200">Admin</Link>
      <ChevronRight className="h-3 w-3 text-slate-400 dark:text-slate-600" />
      <span className="font-medium text-slate-800 dark:text-slate-200">{label}</span>
    </nav>
  );
}

const linkBase = "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors";
const linkActive = "bg-blue-600 text-white shadow-sm shadow-blue-600/30";
const linkIdle = "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white";

export function AdminLayout() {
  const { user, logout, setUser } = useAuth();
  const { t } = useContent();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const badges = useBadges();

  const [theme, setTheme] = useState(() => localStorage.getItem("admin_theme") || "dark");
  useEffect(() => { localStorage.setItem("admin_theme", theme); }, [theme]);

  const isActive = (to, end) => (end ? loc.pathname === to : (loc.pathname === to || loc.pathname.startsWith(to + "/")));
  const activeGroupKey = NAV.find((e) => e.type === "group" && e.items.some((i) => isActive(i.to)))?.key;

  const [expanded, setExpanded] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("admin_nav_expanded") || "null"); if (s) return s; } catch { /* ignore */ }
    return Object.fromEntries(NAV.filter((e) => e.type === "group").map((e) => [e.key, true]));
  });
  useEffect(() => {
    if (activeGroupKey) setExpanded((p) => (p[activeGroupKey] ? p : { ...p, [activeGroupKey]: true }));
  }, [activeGroupKey]);
  const toggle = (key) => setExpanded((p) => { const n = { ...p, [key]: !p[key] }; localStorage.setItem("admin_nav_expanded", JSON.stringify(n)); return n; });

  return (
    <div className={theme === "dark" ? "dark" : ""}>
      <div className="flex min-h-screen bg-slate-50 text-slate-900 dark:bg-[#090d16] dark:text-slate-100">
        <aside className={`fixed inset-y-0 left-0 z-40 flex w-64 transform flex-col border-r border-slate-200 bg-white transition-transform lg:static lg:translate-x-0 dark:border-slate-800 dark:bg-slate-900 ${open ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex h-16 items-center justify-between gap-2 border-b border-slate-200 px-5 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <Logo alt="Logo" className="h-8 w-8 dark:invert" />
              <div>
                <div className="font-display text-sm font-bold leading-none">The World Promotions</div>
                <div className="mt-0.5 flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400"><ShieldCheck className="h-3 w-3" /> {t("admin.brand.tag", "ADMINISTRATION")}</div>
              </div>
            </div>
            <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="admin-close-sidebar"><X className="h-5 w-5 text-slate-400" /></button>
          </div>

          <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
            {hasPerm(user, DASHBOARD.perm) && (
              <Link to={DASHBOARD.to} data-testid="admin-sidebar-/admin" onClick={() => setOpen(false)}
                className={`${linkBase} ${loc.pathname === "/admin" ? linkActive : linkIdle}`}>
                <DASHBOARD.icon className="h-4 w-4" /> {t(DASHBOARD.tkey, DASHBOARD.label)}
              </Link>
            )}

            {NAV.map((e) => {
              if (e.type === "link") {
                if (!hasPerm(user, e.perm)) return null;
                const active = isActive(e.to, e.end);
                return (
                  <Link key={e.to} to={e.to} data-testid={`admin-sidebar-${e.to}`} onClick={() => setOpen(false)}
                    className={`${linkBase} ${active ? linkActive : linkIdle}`}>
                    <e.icon className="h-4 w-4" /> {t(e.tkey, e.label)}
                    <Badge count={e.badgeKey ? badges[e.badgeKey] : 0} testid={e.badgeKey === "alerts" ? "alerts-badge" : `item-badge-${e.to}`} />
                  </Link>
                );
              }
              const visible = e.items.filter((i) => hasPerm(user, i.perm));
              if (!visible.length) return null;
              const isOpen = !!expanded[e.key];
              const secBadge = e.badgeKey ? badges[e.badgeKey] : 0;
              return (
                <div key={e.key} className="mt-1">
                  <button data-testid={`admin-section-${e.key}`} onClick={() => toggle(e.key)}
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${activeGroupKey === e.key ? "text-blue-600 dark:text-blue-400" : "text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"}`}>
                    <e.icon className="h-4 w-4" /><span>{t(e.tkey, e.label)}</span>
                    {!isOpen && <Badge count={secBadge} testid={`section-badge-${e.key}`} />}
                    {isOpen ? <ChevronDown className="ml-auto h-3.5 w-3.5" /> : <ChevronRight className={secBadge ? "h-3.5 w-3.5" : "ml-auto h-3.5 w-3.5"} />}
                  </button>
                  {isOpen && (
                    <div className="mt-0.5 space-y-0.5 pl-2">
                      {visible.map((i) => {
                        const active = isActive(i.to);
                        return (
                          <Link key={i.to} to={i.to} data-testid={`admin-sidebar-${i.to}`} onClick={() => setOpen(false)}
                            className={`${linkBase} py-2 ${active ? linkActive : linkIdle}`}>
                            <i.icon className="h-4 w-4" /> {t(i.tkey, i.label)}
                            <Badge count={i.badgeKey ? badges[i.badgeKey] : 0} testid={i.badgeKey === "alerts" ? "alerts-badge" : `item-badge-${i.to}`} />
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="border-t border-slate-200 p-3 dark:border-slate-800">
            <button onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} data-testid="admin-theme-toggle"
              className="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {theme === "dark" ? t("admin.theme.light", "Mode clair") : t("admin.theme.dark", "Mode sombre")}
            </button>
            <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout-btn"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white">
              <LogOut className="h-4 w-4" /> {t("admin.logout", "Déconnexion")}
            </button>
          </div>
        </aside>

        {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/70 px-5 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/70">
            <div className="flex items-center gap-3">
              <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-open-sidebar"><Menu className="h-6 w-6" /></button>
              <Breadcrumbs />
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden text-sm text-slate-500 md:block dark:text-slate-400">{t("admin.header.connected", "Connecté:")} <span className="font-medium text-slate-900 dark:text-slate-100">{user?.email}</span></div>
              <LanguageSwitcher />
              <PushToggle />
              <StaffNotifBell />
              <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white">{t("admin.header.view_site", "Voir le site →")}</Link>
              <button onClick={() => changeAvatar(setUser, t)} title={t("admin.header.change_photo", "Changer la photo")} data-testid="admin-avatar-btn" className="transition-transform hover:scale-105">
                <UserAvatar user={user} testid="admin-header-avatar" className="h-9 w-9 rounded-full bg-blue-600 text-xs font-bold text-white" />
              </button>
            </div>
          </header>
          <main className="flex-1 p-5 lg:p-8"><WelcomeLangBanner /><Outlet /></main>
        </div>
      </div>
    </div>
  );
}
